package at.theatervogelweide.domain

import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days

/** The ticket reservation page of an act is shown publicly a while before its ticket sale starts. */
object TicketAnnouncement {

    /** How long before the ticket sale starts the ticket reservation page is shown. */
    val PERIOD: Duration = 14.days

    /** From this point on the ticket reservation is shown publicly. All timestamps are in UTC. */
    fun announcedFrom(ticketSaleStart: LocalDateTime): LocalDateTime {
        return (ticketSaleStart.toInstant(TimeZone.UTC) - PERIOD).toLocalDateTime(TimeZone.UTC)
    }

    /**
     * When the ticket reservation page shows an act with [ticketSaleStart] and [performances], or `null` if it never
     * does: without a ticket sale start or performances, or when the sale would start only with the last performance
     * (it could never open). All timestamps are in UTC.
     */
    fun schedule(ticketSaleStart: LocalDateTime?, performances: Collection<LocalDateTime>): TicketSchedule? {
        if (ticketSaleStart == null || performances.isEmpty() || ticketSaleStart >= performances.max()) {
            return null
        }
        return TicketSchedule(
            announcedFrom = announcedFrom(ticketSaleStart),
            ticketSaleStart = ticketSaleStart,
            premiere = performances.min(),
            lastPerformance = performances.max(),
        )
    }

    /**
     * Of [acts], the one the ticket reservation page shows at [now] or, while it isn't announced yet, will show next:
     * of the acts with a [schedule] and a performance after [now], the one whose next performance is the
     * earliest, and of acts playing at the same time the one with the lowest [id], so both apps pick the same whatever
     * the order of [acts]. Used by the admin app's Schedule and the public site's Spielplan & Karten. All timestamps
     * are in UTC.
     */
    fun <T> nextOnTicketPage(
        acts: Iterable<T>,
        now: LocalDateTime,
        id: (T) -> Long,
        ticketSaleStart: (T) -> LocalDateTime?,
        performances: (T) -> Collection<LocalDateTime>,
    ): T? {
        return acts.filter { schedule(ticketSaleStart(it), performances(it)) != null }
            .mapNotNull { act -> performances(act).filter { it > now }.minOrNull()?.let { act to it } }
            .minWithOrNull(compareBy({ it.second }, { id(it.first) }))
            ?.first
    }
}

/** Where an act's ticket sale stands, see [TicketSchedule.saleStateAt]. */
enum class TicketSaleState {
    /** Before [TicketSchedule.announcedFrom]: the ticket page doesn't show the act yet. */
    PLANNED,

    /** Announced, but the sale hasn't started: the ticket page shows the dates and "Kartenverkauf ab …". */
    PENDING,

    /** From the ticket sale's start until the last performance has started: tickets can be reserved. */
    OPEN,

    /** The last performance has started: the ticket page no longer shows the act. */
    OVER,
}

/**
 * The milestones of an act on the ticket reservation page: shown from [announcedFrom], reservable from
 * [ticketSaleStart], until [lastPerformance] has started. While two acts would be shown, the page shows the one whose
 * next performance comes first.
 */
data class TicketSchedule(
    val announcedFrom: LocalDateTime,
    val ticketSaleStart: LocalDateTime,
    val premiere: LocalDateTime,
    val lastPerformance: LocalDateTime,
) {
    /** Whether the page shows the act at [now], leaving other acts aside. */
    fun isShownAt(now: LocalDateTime): Boolean = now >= announcedFrom && now < lastPerformance

    /** Where the ticket sale stands at [now]. */
    fun saleStateAt(now: LocalDateTime): TicketSaleState = when {
        now >= lastPerformance -> TicketSaleState.OVER
        now >= ticketSaleStart -> TicketSaleState.OPEN
        now >= announcedFrom -> TicketSaleState.PENDING
        else -> TicketSaleState.PLANNED
    }
}
